import React from 'react';
import { xorBy } from 'lodash';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Stack, Typography } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { CapacityItem, useCapacitiesLazy } from '@travelity/api';
import { useTranslation } from 'react-i18next';

import { Button, LoadingOverlay } from '@travelity/ui';
import CapacityItemMinimal from './components/capacity-item-minimal';

export interface CapacityModalProps {
    handleCancel: () => void;
    handleConfirm: (v: CapacityItem[]) => void;
    open: boolean;
    isLoading?: boolean;
}

const CapacityModal: React.FC<CapacityModalProps> = ({
    handleCancel,
    handleConfirm,
    open,
    isLoading,
}) => {
    const [selected, setSelected] = React.useState<CapacityItem[]>([]);
    const { t } = useTranslation('common');
    const {
        data: capacities,
        isFetching,
        refetch,
    } = useCapacitiesLazy(undefined, {
        onInvalidPagination: () => {
            refetch();
        },
    });

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            PaperProps={{ sx: { py: 1, bgcolor: '#fff', width: '482px' } }}
        >
            <DialogTitle
                sx={{
                    fontSize: '16px',
                    lineHeight: '16px',
                    fontWeight: 600,
                    color: '#2B395B',
                }}
                id="alert-dialog-title"
            >
                <Typography sx={{ fontWeight: 600 }}>
                    {capacities && capacities.length === 0
                        ? t('No capacities found')
                        : t('Select capacities')}
                </Typography>
            </DialogTitle>
            <DialogContent
                sx={{
                    overflow: 'hidden',
                    pr: 1,
                }}
            >
                <Box
                    sx={{
                        pr: 2,
                        height: 'calc(100% - 76px)',
                        '& > div': {
                            maxHeight: 'calc(100vh - 284px)',
                        },
                    }}
                >
                    <PerfectScrollbar>
                        <Stack
                            sx={{
                                pr: 0.5,
                                pb: 1,
                            }}
                            gap={1}
                        >
                            {capacities?.map(capacity => (
                                <CapacityItemMinimal
                                    key={capacity.capacityId}
                                    capacity={capacity}
                                    isSelected={
                                        !!selected.find(
                                            s =>
                                                s.capacityId ===
                                                capacity.capacityId
                                        )
                                    }
                                    onSelect={() =>
                                        setSelected(
                                            xorBy(selected, [capacity], 'id')
                                        )
                                    }
                                />
                            ))}
                        </Stack>
                    </PerfectScrollbar>
                </Box>
                {(isFetching || isLoading) && (
                    <LoadingOverlay
                        title={t('Please wait...')}
                        subTitle={t('It’ll just take a moment.')}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 1)',
                        }}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'end' }}>
                <Stack direction="row" gap={1}>
                    <Button
                        color="neutral"
                        variant="contained"
                        onClick={handleCancel}
                        sx={{
                            px: '26px',
                            py: '10px',
                        }}
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={isFetching || isLoading || !selected.length}
                        onClick={() => {
                            handleConfirm(selected);
                        }}
                        sx={{
                            py: '10px',
                            px: 4,
                        }}
                    >
                        {t('Confirm')}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default CapacityModal;
